import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import { useAuth } from '../contexts/AuthContext';
import ReportModal from '../components/moderation/ReportModal';
import { ReportContentType } from 'api/types/moderation';
import Button from '../components/Button';
import TestEdildiBadge from '../components/TestEdildiBadge';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { listingService } from 'api/services/ListingService';
import { favoriteListingService } from 'api/services/FavoriteListingService';
import { conversationService } from 'api/services/ConversationService';
import {
  type ListingDetailProduct,
  listingViewModelToDetailProduct,
} from 'api/utils/listingDetailMapper';
import ListingSpecsSection from '../components/Listing/ListingSpecsSection';
import LocationMapEmbed from '../components/Listing/LocationMapEmbed';
import { usePerformanceTest } from '../hooks/usePerformanceTest';
import { ApiResultBlock } from '../components/ListingPerformance/ApiResultBlock';
import ListingReelsSection from '../components/reels/ListingReelsSection';
import ShareListingModal from '../components/ShareListingModal';

const Ilan_detay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [product, setProduct] = useState<ListingDetailProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [showPhone, setShowPhone] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [carousel, setCarousel] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const performanceTest = usePerformanceTest({
    specs: product?.specs ?? {},
  });

  const fetchListing = useCallback(async (listingId: string) => {
    setLoading(true);
    setFetchError(null);
    try {
      const result = await listingService.getById(listingId);
      if (result.isSuccess && result.data) {
        setProduct(listingViewModelToDetailProduct(result.data));
        setIsFavorite(result.data.isUserFavorite ?? false);
        listingService.incrementView(listingId).catch(() => {});
      } else {
        setProduct(null);
        setFetchError(result.message ?? 'İlan bulunamadı.');
      }
    } catch (err) {
      setProduct(null);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchListing(id);
    } else {
      setLoading(false);
      setFetchError('İlan kodu belirtilmedi.');
    }
  }, [id, fetchListing]);

  useEffect(() => {
    if (carouselRef.current) {
      const bsCarousel = new (window as any).bootstrap.Carousel(carouselRef.current);
      setCarousel(bsCarousel);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setIsLoggedIn(loggedIn);
  }, []);

  const handleGameSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGame(e.target.value);
  };

  const handleAppSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedApp(e.target.value);
  };

  const handleTestGame = () => {
    if (!selectedGame) {
      alert('Lütfen önce bir oyun seçiniz.');
      return;
    }
    performanceTest.fps.run(selectedGame);
  };

  const handleTestApp = () => {
    if (!selectedApp) {
      alert('Lütfen önce bir uygulama seçiniz.');
      return;
    }
    performanceTest.compatibility.run(selectedApp);
  };

  const handleShowPhone = () => {
    if (!isAuthenticated) {
      alert('Telefon numarasını görmek için giriş yapmalısınız.');
      navigate('/giris');
      return;
    }
    setShowPhone(true);
  };

  const handleToggleFavorite = async () => {
    if (!user?.id) {
      navigate('/giris');
      return;
    }
    if (!product) return;
    const result = await favoriteListingService.toggle({
      userId: user.id,
      listingId: product.id,
    });
    if (result.isSuccess) setIsFavorite((prev) => !prev);
    else if (result.message) window.alert(result.message);
  };

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  const showSlide = (index: number) => {
    if (carousel) {
      carousel.to(index);
    }
  };

  const handleStartChat = async () => {
    if (!isLoggedIn) {
      alert('Mesaj göndermek için giriş yapmalısınız.');
      navigate('/giris');
      return;
    }
    if (!id) {
      navigate('/mesajlar');
      return;
    }
    try {
      const result = await conversationService.contactSeller(id);
      if (result.isSuccess && result.data?.id) {
        navigate(`/mesajlar?conversationId=${result.data.id}`);
      } else {
        alert(result.message ?? 'İletişim başlatılamadı.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'İletişim başlatılamadı.');
    }
  };

  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleShareListing = () => {
    setShowShareModal(true);
  };

  if (loading) {
    return (
      <div className="container-fluid p-0">
        <div className="container py-5 text-center text-muted">
          <div className="spinner-border text-primary mb-2" role="status" />
          <p>İlan yükleniyor...</p>
        </div>
      </div>
    );
  }

  const isListingOwner =
    !!(user?.id && product?.sellerId && user.id === product.sellerId);

  if (fetchError || !product) {
    return (
      <div className="container-fluid p-0">
        <div className="container py-5 text-center">
          <p className="text-danger mb-3">{fetchError ?? 'İlan bulunamadı.'}</p>
          <Link className="btn btn-primary" to="/">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <header className="header sticky-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
        <nav className="navbar navbar-expand-lg">
          <div className="container">
            {/* Logo */}
            <Link className="navbar-brand fw-bold text-primary" to="/">
              <i className="fas fa-laptop me-2"></i>SadeceBilgisayar.com
            </Link>

            {/* Arama Kutusu */}
            <div className="search-box mx-lg-2 flex-grow-1" style={{ maxWidth: "75%", marginLeft: "-10px" }}>
              <form className="d-flex">
                <div className="input-group">
                  <input
                    aria-label="Arama"
                    className="form-control"
                    placeholder="Dizüstü, masaüstü, parça ara..."
                    type="search"
                  />
                  <Button className="btn btn-primary" type="submit">
                    <i className="fas fa-search" />
                  </Button>
                </div>
              </form>
            </div>

            {/* Sağ Menü */}
            <div className="nav-right">
              <Link className="btn btn-outline-primary me-2" to="/ilan-ver">
                <i className="fas fa-plus-circle me-2" />
                İlan Ver
              </Link>
              <Link className="btn btn-primary" to="/">
                <i className="fas fa-user me-2" />
                Giriş Yap
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mt-4">
        <div className="row">
          {/* Sol Taraf - Resim Galerisi ve Açıklama */}
          <div className="col-md-8 mb-4">
            {/* Ana Carousel */}
            <div className="carousel slide mb-3" data-bs-ride="false" id="productCarousel" ref={carouselRef}>
              <div className="carousel-inner rounded">
                {product.images.map((image, index) => (
                  <div key={index} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                    <img alt={`Ürün Görseli ${index + 1}`} className="d-block w-100" src={image} />
                  </div>
                ))}
              </div>
              <Button className="carousel-control-prev custom-carousel-control" type="button" data-bs-target="#productCarousel" data-bs-slide="prev">
                <div className="carousel-control-icon">
                  <i className="fas fa-chevron-left"></i>
                </div>
              </Button>
              <Button className="carousel-control-next custom-carousel-control" type="button" data-bs-target="#productCarousel" data-bs-slide="next">
                <div className="carousel-control-icon">
                  <i className="fas fa-chevron-right"></i>
                </div>
              </Button>
            </div>

            {/* Thumbnail Gallery */}
            <div className="thumbnail-gallery-container">
              <div className="thumbnail-gallery d-flex gap-2 overflow-auto px-2 py-1">
                {product.images.map((image, index) => (
                  <div key={index} className="thumbnail" onClick={() => showSlide(index)}>
                    <img alt={`Thumbnail ${index + 1}`} className="rounded" src={image} />
                    <div className="thumbnail-overlay"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature 009 (US3): reels associated with this listing. */}
            <ListingReelsSection listingId={product.id} />

            <ListingSpecsSection specs={product.specs} />

            {/* Açıklama Kartı */}
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-align-left me-2 text-primary" />
                  Açıklama
                </h5>
                <p>{product.description}</p>
              </div>
            </div>

            {/* Konum Kartı */}
            <div className="card mt-4">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  İlan Konumu
                </h5>
                <p className="mb-3">
                  <strong>Konum:</strong> {[product.location.district, product.location.city].filter(Boolean).join(', ') || 'Belirtilmemiş'}
                  {product.location.address ? ` – ${product.location.address}` : ''}
                </p>
                
                {(product.location.district || product.location.city) && (
                  <LocationMapEmbed
                    query={[product.location.address, product.location.district, product.location.city]
                      .filter(Boolean)
                      .join(', ')}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Sağ Taraf - İlan Bilgileri */}
          <div className="col-md-4">
            {/* Satıcı Bilgileri */}
            <div className="card seller-info mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <img alt="Satıcı" className="rounded-circle me-3" height="50" src={product.seller.avatar} width="50" />
                  <div>
                    <Link
                      className="text-decoration-none"
                      to={
                        product.sellerId
                          ? product.seller.accountType === 'Corporate'
                            ? `/satici-profil-kurumsal/${product.sellerId}`
                            : `/satici-profil/${product.sellerId}`
                          : '#'
                      }
                    >
                      <h5 className="mb-0">{product.seller.name}</h5>
                    </Link>
                    <small className="text-muted">Üye: {product.seller.joinDate}</small>
                  </div>
                </div>
                <div className="d-grid gap-2">
                  <Button className="btn btn-primary" onClick={handleStartChat}>
                    <i className="fas fa-comment me-2"></i>
                    Mesaj Gönder
                  </Button>
                  {product.showPhoneOnListing ? (
                    <Button
                      className="btn btn-outline-primary"
                      onClick={handleShowPhone}
                      disabled={showPhone && Boolean(product.sellerPhoneNumber)}
                    >
                      <i className="fas fa-phone me-2"></i>
                      {showPhone
                        ? product.sellerPhoneNumber || 'Telefon numarası kayıtlı değil'
                        : 'Telefonu Göster'}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* İlan Bilgileri */}
            <div className="card listing-info mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h1 className="h3 mb-0">{product.title}</h1>
                  {!isListingOwner && (
                    <Button
                      type="button"
                      className="btn btn-outline-primary btn-sm favorite-btn"
                      style={{ backgroundColor: 'var(--sb-bg)' }}
                      onClick={handleToggleFavorite}
                    >
                      <i className={`${isFavorite ? 'fas' : 'far'} fa-heart ${isFavorite ? 'text-danger' : 'text-primary'}`} />
                    </Button>
                  )}
                </div>
                {product.isTested && (
                  <div className="mb-3">
                    <TestEdildiBadge />
                  </div>
                )}
                <div className="price-badge mb-3 fs-4 fw-bold text-primary border border-primary rounded-2 px-2 py-1 text-center" style={{ width: 'fit-content' }}>
                  {product.price.toLocaleString('tr-TR')} ₺
                </div>
                <div className="text-muted small mb-2">
                  <i className="fas fa-hashtag me-1 text-primary"></i>
                  İlan Kodu:{' '}
                  <span className="fw-bold text-body">
                    {product.listingCode ?? product.id}
                  </span>
                </div>
                <div className="listing-meta text-muted mb-3">
                  {product.productTypeName && (
                    <div>
                      <i className="fas fa-tag me-2 text-primary"></i>
                      {product.productTypeName}
                    </div>
                  )}
                  {product.conditionName && (
                    <div>
                      <i className="fas fa-box me-2 text-primary"></i>
                      {product.conditionName}
                    </div>
                  )}
                  {(product.location.district || product.location.city) && (
                    <div>
                      <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                      {[product.location.district, product.location.city].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {product.viewCount != null && (
                    <div>
                      <i className="fas fa-eye me-2 text-primary"></i>
                      {product.viewCount} görüntülenme
                    </div>
                  )}
                </div>
                <hr />
                <div className="listing-actions mt-3">
                  <div className="btn-group w-100">
                    <Button
                      ref={shareButtonRef}
                      className="btn btn-outline-primary"
                      onClick={handleShareListing}
                    >
                      <i className="fas fa-share-alt me-2"></i>
                      Paylaş
                    </Button>
                    <Button className="btn btn-outline-danger" data-bs-target="#complaintModal" data-bs-toggle="modal">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      Şikayet Et
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Performans Kartı - Oyunlar (API'den veri gelirse göster) */}
            {Object.keys(product.performance.games).length > 0 && (
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-4">
                    <i className="fas fa-gamepad me-2 text-primary"></i>
                    Oyun Performansı
                  </h5>
                  <label htmlFor="gameSelector" className="form-label">Oyun Seçin</label>
                  <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 mb-3 sb-performance-actions">
                    <select className="form-select flex-grow-1 min-w-0" id="gameSelector" value={selectedGame} onChange={handleGameSelect}>
                      <option value="">Oyun seçiniz...</option>
                      {Object.keys(product.performance.games).map(game => (
                        <option key={game} value={game}>{getGameName(game)}</option>
                      ))}
                    </select>
                    <div className="d-grid d-sm-block flex-shrink-0">
                      <Button
                        type="button"
                        className="btn btn-primary"
                        id="testGameBtn"
                        onClick={handleTestGame}
                        disabled={performanceTest.fps.loading}
                        style={{ fontWeight: 'bold' }}
                      >
                        {performanceTest.fps.loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Test Ediliyor...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-play-circle me-2"></i>
                            Test Et
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <ApiResultBlock
                    loading={performanceTest.fps.loading}
                    result={performanceTest.fps.result}
                    error={performanceTest.fps.error}
                  />
                </div>
              </div>
            )}

            {/* Performans Kartı - İş Uygulamaları (API'den veri gelirse göster) */}
            {Object.keys(product.performance.apps).length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="fas fa-briefcase me-2 text-primary"></i>
                  İş Uygulamaları Uyumluluğu
                </h5>

                <label htmlFor="appSelector" className="form-label">İş Uygulaması Seçin</label>
                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 mb-3 sb-performance-actions">
                  <select className="form-select flex-grow-1 min-w-0" id="appSelector" value={selectedApp} onChange={handleAppSelect}>
                    <option value="">Uygulama seçiniz...</option>
                    {Object.keys(product.performance.apps).map(app => (
                      <option key={app} value={app}>{getAppName(app)}</option>
                    ))}
                  </select>
                  <div className="d-grid d-sm-block flex-shrink-0">
                    <Button
                      type="button"
                      className="btn btn-primary"
                      id="testAppBtn"
                      onClick={handleTestApp}
                      disabled={performanceTest.compatibility.loading}
                      style={{ fontWeight: 'bold' }}
                    >
                      {performanceTest.compatibility.loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Test Ediliyor...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-play-circle me-2"></i>
                          Test Et
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <ApiResultBlock
                  loading={performanceTest.compatibility.loading}
                  result={performanceTest.compatibility.result}
                  error={performanceTest.compatibility.error}
                />

                {/* Uygulama Uyumluluk Göstergesi */}
                {selectedApp && product.performance.apps[selectedApp] && (
                  <div id="appDisplay">
                    <div className="app-compatibility mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-1" id="appName">{getAppName(selectedApp)}</h6>
                          <p className="text-muted small mb-0" id="appRequirements">
                            Tavsiye Edilen Sistem Gereksinimleri
                          </p>
                        </div>
                        <div className={`badge ${product.performance.apps[selectedApp].compatible ? 'bg-success' : 'bg-danger'}`} id="compatibilityBadge">
                          {product.performance.apps[selectedApp].compatible ? 'Destekleniyor' : 'Desteklenmiyor'}
                        </div>
                      </div>
                    </div>
                    <div className="app-details mt-3">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="performance-detail mb-2">
                            <i className="fas fa-check-circle text-success me-2"></i>
                            <span id="appFeature1">{product.performance.apps[selectedApp].requirements}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer mt-auto py-5 border-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3 text-center">
              <h5 className="mb-3">Hakkımızda</h5>
              <p className="text-secondary">
                SadeceBilgisayar.com, sıfır veya İkinci el teknolojik ürünleri güvenilir bir şekilde alım veya satım yapabileceğiniz bir platformdur.
              </p>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3">Önemli Tavsiyeler</h5>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline text-nowrap" to="/masaustu_tavsiyeler">
                    Masaüstü alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/laptop-tavsiyeler">
                    Laptop alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
                <li className="mb-2">
                  <Link className="text-secondary text-decoration-none d-inline" to="/donanim-tavsiyeler">
                    Donanım alırken dikkat edilmesi gerekenler
                  </Link>
                </li>
              </ul>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3">İletişim</h5>
              <div className="contact-info">
                <p className="text-secondary mb-2">Görüşleriniz ve şikayetleriniz için</p>
                <p className="text-secondary mb-2">
                  <i className="fas fa-envelope me-2"></i>
                  info@sadecebilgisayar.com
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center">
              <h5 className="mb-3">Sosyal Medya Hesaplarımız</h5>
              <div className="social-links">
                <a className="text-primary me-4" href="#">
                  <i className="fab fa-facebook fa-3x"></i>
                </a>
                <a className="text-danger me-4" href="#">
                  <i className="fab fa-instagram fa-3x"></i>
                </a>
                <a className="text-danger" href="#">
                  <i className="fab fa-youtube fa-3x"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom mt-5 pt-4 border-top">
            <div className="row">
              <div className="col-md-6">
                <p className="text-secondary mb-0">© 2024 SadeceBilgisayar.com - Tüm hakları saklıdır.</p>
              </div>
              <div className="col-md-6 text-md-end">
                <Link className="text-secondary me-3" to="/gizlilik-politikasi">Gizlilik Politikası</Link>
                <Link className="text-secondary me-3" to="/kullanici-sozlesmesi">Kullanım Koşulları</Link>
                <Link className="text-secondary" to="/cerez-politikasi">Çerez Politikası</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <ReportModal
        contentId={product.id}
        contentType={ReportContentType.Listing}
        reportedUserId={product.sellerId}
        isAuthenticated={isAuthenticated}
        isOwnContent={Boolean(user?.id && product.sellerId && user.id === product.sellerId)}
        onRequireLogin={() => {
          window.alert('Şikayet için giriş yapmalısınız.');
          navigate('/giris');
        }}
      />

      <ShareListingModal
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        listingId={product.id}
        listingTitle={product.title}
        triggerRef={shareButtonRef}
      />

      {/* Video Modal */}
      <div className="modal fade" id="videoModal" tabIndex={-1}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-0">
              <Button className="btn-close position-absolute end-0 top-0 m-2 z-3" style={{ backgroundColor: 'var(--sb-bg)' }} data-bs-dismiss="modal" type="button"></Button>
              <video className="w-100" controls id="productVideo">
                <source src="../assets/videos/product-video.mp4" type="video/mp4"/>
                Tarayıcınız video etiketini desteklemiyor.
              </video>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .thumbnail-gallery-container {
            background-color: var(--sb-bg-muted);
            border-radius: 8px;
          }

          .thumbnail-gallery {
            scrollbar-width: thin;
            scrollbar-color: var(--sb-border) var(--sb-bg);
          }

          .thumbnail-gallery::-webkit-scrollbar {
            height: 4px;
          }

          .thumbnail-gallery::-webkit-scrollbar-track {
            background: transparent;
          }

          .thumbnail-gallery::-webkit-scrollbar-thumb {
            background-color: #adb5bd;
            border-radius: 4px;
          }

          .thumbnail {
            position: relative;
            flex: 0 0 auto;
            width: 70px;
            height: 70px;
            border-radius: 6px;
            overflow: hidden;
          }

          .thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .thumbnail-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.3);
            transition: all 0.2s ease;
          }

          .thumbnail:hover .thumbnail-overlay {
            background-color: rgba(0, 0, 0, 0.1);
          }

          .thumbnail.active {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          }

          .thumbnail.active .thumbnail-overlay {
            background-color: rgba(13, 110, 253, 0.2);
            border: 2px solid #0d6efd;
          }

          .custom-carousel-control {
            width: 10%;
            opacity: 1;
          }

          .carousel-control-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background-color: var(--sb-bg);
            border-radius: 50%;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
          }

          .carousel-control-icon i {
            color: #000;
            font-size: 1.2rem;
          }

          .custom-carousel-control:hover .carousel-control-icon {
            background-color: var(--sb-bg);
            transform: scale(1.1);
          }

          .carousel-inner {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .carousel-item {
            background-color: var(--sb-bg-muted);
          }

          .carousel-item img {
            object-fit: contain;
            height: 400px;
          }

          .video-thumb {
            position: relative;
            background-color: #000;
          }

          .video-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 24px;
            height: 24px;
            background-color: var(--sb-bg);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2;
            transition: all 0.3s ease;
          }

          .video-icon i {
            color: #000;
            font-size: 12px;
            margin-left: 2px;
          }

          .video-thumb:hover .video-icon {
            transform: translate(-50%, -50%) scale(1.1);
            background-color: var(--sb-bg);
          }

          #videoModal .modal-content {
            background-color: #000;
          }

          #videoModal .btn-close {
            opacity: 0.8;
          }

          #videoModal .btn-close:hover {
            opacity: 1;
          }

          /* Test Et Button Styles */
          #testGameBtn, #testAppBtn {
            padding: 8px 16px;
            font-weight: 600;
            transition: all 0.3s ease;
            min-width: 100px;
          }

          #testGameBtn:hover, #testAppBtn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
          }
        `}
      </style>
    </div>
  );
};

// Yardımcı fonksiyonlar
const getGameName = (key: string): string => {
  const names: { [key: string]: string } = {
    gta5: 'GTA 5',
    rust: 'Rust',
    csgo: 'CS:GO',
    valorant: 'Valorant',
    fortnite: 'Fortnite',
    rdr2: 'Red Dead Redemption 2',
    cyberpunk: 'Cyberpunk 2077',
    warzone: 'Call of Duty: Warzone'
  };
  return names[key] || key;
};

const getAppName = (key: string): string => {
  const names: { [key: string]: string } = {
    photoshop: 'Adobe Photoshop 2024',
    premiere: 'Adobe Premiere Pro 2024',
    aftereffects: 'Adobe After Effects 2024',
    illustrator: 'Adobe Illustrator 2024',
    office365: 'Microsoft 365 Suite',
    excel: 'Microsoft Excel (Büyük Veri)',
    powerbi: 'Microsoft Power BI',
    autocad: 'AutoCAD 2024',
    '3dsmax': '3ds Max 2024',
    revit: 'Revit 2024',
    cubase: 'Cubase Pro 12',
    protools: 'Pro Tools Studio',
    flstudio: 'FL Studio 21'
  };
  return names[key] || key;
};

export default Ilan_detay;
